<template>
  <RevDrawer
    :close-button-label="i18n(translations.cancel)"
    :name="modalName"
    :title="i18n(translations.drawerTitle)"
    @close="handleClose"
  >
    <template #body>
      <div class="mb-8 mt-16">
        {{ i18n(translations.uploadReceiptForOrder) }}
      </div>

      <RevInputFileUpload
        id="input-file"
        v-model="uploadedFiles"
        :accept="['.pdf']"
        :label-empty="i18n(translations.uploadReceipt)"
        :max-files="1"
      />

      <RevInfoBlock
        v-if="apiError"
        class="my-16"
        :icon="IconWarning"
        :title="apiError.title"
        variant="warning"
      >
        {{ apiError.detail }}
      </RevInfoBlock>
    </template>

    <template #footer>
      <div class="flex flex-col items-center">
        <RevButton
          class="mb-16"
          :disabled="!canUpload"
          full-width="always"
          :loading="isUploading"
          variant="primary"
          @click="handleUploadReceipt"
        >
          {{ i18n(translations.uploadReceipt) }}
        </RevButton>

        <RevLink @click="handleCancel">
          {{ i18n(translations.cancel) }}
        </RevLink>
      </div>
    </template>
  </RevDrawer>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { handleFileUpload } from '@backmarket/front-upload'
import { HttpApiError } from '@backmarket/http-api'
import { patchMerchantOrdersDetails } from '@backmarket/http-api/src/api-specs-seller-backoffice/merchants-orders/merchant-orders-page'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { getHttpBaseUrl } from '@backmarket/nuxt-module-http/getHttpBaseUrl'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevDrawer } from '@ds/components/Drawer'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevInputFileUpload } from '@ds/components/InputFileUpload'
import { RevLink } from '@ds/components/Link'
import { closeModal } from '@ds/components/ModalBase'
import { IconWarning } from '@ds/icons/IconWarning'

import { MODAL_NAME } from './SendCustomerReceiptDrawer.constants'
import translations from './SendCustomerReceiptDrawer.translations'

const { openSuccessToast, openErrorToast } = useTheToast()

const props = defineProps<{ orderId: number; refreshOrders: () => void }>()

const i18n = useI18n()

const uploadedFiles = ref([])
const isUploading = ref(false)
const apiError = ref<null | HttpApiError>(null)

const canUpload = computed(() => uploadedFiles.value.length >= 1)
const modalName = computed(() => MODAL_NAME + props.orderId)

function resetToInitialState() {
  apiError.value = null
  uploadedFiles.value = []
}

function handleClose() {
  resetToInitialState()
}
function handleCancel() {
  resetToInitialState()
  closeModal(modalName.value)
}

async function handleUploadReceipt() {
  isUploading.value = true
  apiError.value = null

  try {
    const uploadedFile = await handleFileUpload(
      `${getHttpBaseUrl()}/services/uploads`,
      'care_merchant_invoice',
      null,
      uploadedFiles.value[0],
    )

    if (uploadedFile.status === 'success') {
      await $httpFetch(patchMerchantOrdersDetails, {
        pathParams: {
          orderId: props.orderId,
        },
        body: {
          invoiceDocumentHandle: uploadedFile.handle,
        },
      })

      openSuccessToast({
        title: i18n(translations.successTitle),
        content: i18n(translations.successContent),
      })
      closeModal(modalName.value)
      props.refreshOrders()
    } else {
      openErrorToast()
    }
  } catch (error) {
    apiError.value = HttpApiError.fromAnyError(error as Error)
  } finally {
    isUploading.value = false
  }
}
</script>
